import get from 'lodash/get';
import { pathToTreeKeys } from '../../applyMenuConfigChanges/deleteMembers';
import { toUrlFormat } from '..';
import { serviceDefaultDataMembers } from '../createTagsSubTreesVisitor';
import { getName, isNode } from '../helpers';
import { NODE_TYPES } from '../../typeUtils';
import { TYPES } from '../../flattenTree/utils/consts';
import { sectionPrefix } from '../../../client/components/NavigationMenu/utils';
import { getUrlFormatTitle } from '../../../client/components/NavigationMenu/MenuTopSections/ArticlesSections/utils';
import { extractTag } from '../../flattenTree/utils/extractors';

const countDots = (path) => (path.match(/./g) || []).length;

export const getGuidesSortedByTagsDepth = (guides) => {
  // TODO: remove this function if it's not used.
  return [...guides].sort(({ tag: aTag = '' }, { tag: bTag = '' }) => {
    if (countDots(aTag) > countDots(bTag)) {
      return 1;
    }
    if (countDots(aTag) < countDots(bTag)) {
      return -1;
    }
    return 0;
  });
};

export const getTagsToCreate = (guides) => {
  return Array.from(new Set(guides.map((g) => g.tag).filter((_) => !!_)));
};

const createTag = (node, path, tagName, tagSortOrder, subTag) => {
  const nodeToCreateKey = path.pop();
  const targetNode = path.length ? get(node, path, false) : node;
  const isArticlesCategory = node.data?.propType === TYPES.ARTICLES_CATEGORY;
  targetNode[nodeToCreateKey] = {
    data: serviceDefaultDataMembers(
      subTag,
      toUrlFormat(subTag),
      isArticlesCategory,
      tagSortOrder
    ),
    type: NODE_TYPES.TAG,
  };
};

const createAllNodesInPath = (node, tagName, tagSortOrder) => (subTag, i) => {
  const treePath = pathToTreeKeys(toUrlFormat(tagName));
  const currentPath = treePath.slice(0, i + 1);
  const targetNode = get(node, currentPath, false);
  if (!targetNode) {
    createTag(node, currentPath, tagName, tagSortOrder, subTag);
  }
};

export const createTagsGivenPaths = (node, tags, isForArticlesSection) => {
  tags.forEach((tagName, tagIndex) => {
    const subTags = tagName.split('.');
    const tagSortOrder = isForArticlesSection && tagIndex;
    subTags.forEach(createAllNodesInPath(node, tagName, tagSortOrder));
  });
};

export const getArticleTopSectionWithTagPrefix = (titleKey, tag) => {
  const tagUrl = toUrlFormat(tag).replace(/\./g, '/');
  return sectionPrefix(titleKey, tagUrl);
};

export const getArticlesSectionGuideWithTagLink = (title, relatedGuide) => {
  const titleKey = getUrlFormatTitle(title);
  const prefix = getArticleTopSectionWithTagPrefix(
    titleKey,
    extractTag(relatedGuide)
  );
  return `${prefix}/${toUrlFormat(relatedGuide.name)}`;
};

const addGuideToTagNode = (
  node,
  { tag, ...relatedGuideProps },
  isForArticlesSection
) => {
  const treeKeys = pathToTreeKeys(toUrlFormat(tag));
  const targetNode = get(node, treeKeys);
  if (!targetNode) {
    throw new Error(
      `there is no path from ${node.data.name} to ${treeKeys.join('.')}`
    );
  }

  if (relatedGuideProps.isExternalLink) {
    targetNode.data.relatedGuides.push({ ...relatedGuideProps });
  } else {
    const link = isForArticlesSection
      ? getArticlesSectionGuideWithTagLink(getName(node.data), {
          tag,
          ...relatedGuideProps,
        })
      : relatedGuideProps.link;

    targetNode.data.relatedGuides.push({
      ...relatedGuideProps,
      link,
    });
  }
};

export const populateGuidesInRelevantNodes = (node, isForArticlesSection) => {
  node.data.relatedGuides.forEach((relatedGuide) => {
    if (relatedGuide.tag) {
      addGuideToTagNode(node, relatedGuide, isForArticlesSection);
    }
  });
};

export const hasGuides = (node) => !!node?.data?.relatedGuides?.length;

export const setUrlFormatOnRelatedGuidesIfNeeded = (node) => {
  node.data.relatedGuides.forEach((guide) => {
    if (!guide.urlFormat) {
      guide.urlFormat = toUrlFormat(guide.name);
    }
  });
};

export const replaceGroupFieldToTagIfNeeded = (node) => {
  /*
  In guides.json we use group field instead of tag for better UX,
  here we replace the group field to tag so we can use the same logic as the tags.
 */
  node.data.relatedGuides.forEach((guide) => {
    if (guide.group) {
      guide.tag = guide.group;
      delete guide.group;
    }
  });
};

export const addGuidesToNodesVisitor = (node, isForArticlesSection) => {
  /*
    if the node has relatedGuides:
      1. if isForArticlesSection (it's will be true if we apply this visitor on the guidesTree):
        a. replace "group" field to "tag" for consistency.
        b. set urlFormat on the relevant guides if needed.

      2. create the new default tags (entries) in the tree according to the relatedGuides tag field
      3. move all the guides from the node to the relevant tag.
      4. clean from the node the relatedGuides that we moved to the tags.
  */

  if (isNode(node)) {
    if (hasGuides(node)) {
      if (isForArticlesSection) {
        replaceGroupFieldToTagIfNeeded(node);
        setUrlFormatOnRelatedGuidesIfNeeded(node);
      }
      const tags = getTagsToCreate(node.data.relatedGuides); // extract the relevant tags from relatedGuides.
      createTagsGivenPaths(node, tags, isForArticlesSection); // create the new default tags (entries) in the tree according to the relatedGuides tag field
      populateGuidesInRelevantNodes(node, isForArticlesSection); // move all the guides from the node to the relevant tag.
      node.data.relatedGuides = node.data.relatedGuides.filter((_) => !_.tag); // clean from the node the relatedGuides that we moved to the tags.
    } else {
      node.data.relatedGuides = [];
    }
  }
};
